import React, { useState, useEffect, useCallback } from 'react';
import styled, { css } from 'styled-components';

// --- Styled Components (Renamed) ---

const FormSection = styled.section`
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  height: 100%;

  @media (max-width: 640px) {
    padding-top: 16px;
    padding-bottom: 16px;
  }
`;

const SongFormSection = styled(FormSection)`
  @media (max-width: 375px) {
    padding-left: 12px;
    padding-right: 12px;
  }
`;

const LayoutRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 640px) {
    gap: 12px;
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const SectionHeader = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: #333;
  margin: 0 0 16px 0;
  text-align: center;
`;

const baseInputStyle = css`
  width: 100%;
  padding: 10px 4px;
  border: none;
  border-bottom: 2px solid #ccc;
  font-size: 1rem;
  font-weight: 700;
  background-color: transparent;
  transition: border-color 0.2s ease;
  border-radius: 0;

  &:focus {
    outline: none;
    border-bottom-color: #111;
  }

  &:disabled {
    background-color: #f0f0f0;
    border-bottom-color: #d0d0d0;
  }
`;

const TextInput = styled.input`${baseInputStyle}`;
const Dropdown = styled.select`${baseInputStyle}`;

const ButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
`;

const OptionButton = styled.button`
  cursor: pointer;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 900;
  transition: all 0.2s ease;
  background-color: #fff;
  color: #555;
  flex-grow: 1;
  flex-basis: 0;
  min-width: 0;

  &.selected {
    background-color: #111;
    color: white;
    border-color: #111;
  }
`;

const SongOptionButton = styled(OptionButton)`
  @media (max-width: 360px) {
    font-size: 0.8rem;
  }
  @media (max-width: 350px) {
    font-size: 0.75rem;
  }
`;

const PolicyGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    
    & > div:first-child {
      margin-bottom: 24px; 
    }
  }
`;

const PolicyColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CheckboxWrapper = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  font-weight: 550;

  &:hover {
    background-color: #f7f7f7;
  }

  input {
    margin-right: 10px;
    width: 16px;
    height: 16px;
    accent-color: #111;
  }
`;

// Data Structures

const lobbyPolicies = {
  allowProxy: { text: '대리모집해주면감사', value: '代理助かります🙏' },
  freeDifficulty: { text: '난이도 자유', value: '難易度自由' },
  highSpeed: { text: '고속주회 협력 부탁드립니다.', value: '高速周回のご協力お願いします🚗³₃' },
  noFireExtinguish: { text: '불빼기×', value: '火消し×' },
  noMaster: { text: '앵콜 목적 이외의 마스터 금지', value: 'アンコ目的以外での紫禁止' },
  noExpert: { text: '앵콜 목적 이외의 익스 이상 금지', value: 'アンコ目的以外での赤以上禁止' },
  allowOffMetaSongs: { text: '효율 곡 이외의 곡 선택 가능', value: '効率曲以外選曲可' },
  songSelectable: { text: '선곡 가능', value: '選曲◎' },
  watchMv: { text: 'MV 나오면 봄', value: 'MV来たら観ます' },
  allowCostumeChange: { text: '옷 갈아입기 가능', value: 'お着替え◎' },
  noDefaultCostume: { text: '기본옷X', value: '初期衣装❌' },
  ignoreScoreFever: { text: 'SF 신경 안 씀', value: 'SFなし' },
  missesOk: { text: '실수 신경 안 씀', value: 'ミス気にしません' },
  endOnHostSignal: { text: '방장의 ‘오츠카레사마’에 해산', value: '主のおつさきで解散' },
  sameStamps: { text: '스탬프 다른 방과 동일', value: 'スタンプ他部屋と同じ' },
  dissolveOnMismatch: { text: '조건 다를 시 해산', value: '条件違い解散' },
  longPlayWelcome: { text: '장시간 환영', value: '長時間歓迎' },
  leavingMidwayOk: { text: '중도 퇴장 OK', value: '途中退室OK' },
  noLeavingMidway: { text: '중도 퇴장 NG', value: '途中退室NG' },
  finishSession: { text: '끝까지 할 수 있는 분', value: '最後までできる方' },
  waitForAll: { text: '모일 때까지 기다릴 수 있는 분', value: '集まるまで待てる方' },
  lowerReqsIfSlow: { text: '모이지 않으면 조건 내립니다.', value: '集まり悪ければ下げます' },
  disbandIfSlow: { text: '모이지 않으면 해산합니다', value: '集まり悪ければ解散します' },
  disconnectOkOnLastSong: { text: '막판 안 치고 나가도 O', value: 'ラスト曲始まってから切断〇' },
};

const songConfigurations = {
  '🦐': { label: '엔비🦐', rules: ['ignoreScoreFever', 'sameStamps', 'highSpeed', 'longPlayWelcome', 'noFireExtinguish', 'dissolveOnMismatch'] },
  'ロスエン': { label: '로앤파', rules: ['ignoreScoreFever', 'sameStamps', 'dissolveOnMismatch', 'noLeavingMidway', 'waitForAll', 'disconnectOkOnLastSong'] },
  'おまかせ': { label: '오마카세', rules: ['ignoreScoreFever', 'sameStamps', 'missesOk', 'dissolveOnMismatch'] },
  'MV': { label: 'MV', rules: ['ignoreScoreFever', 'sameStamps', 'freeDifficulty', 'noDefaultCostume', 'allowCostumeChange'] },
  'default': { label: '기본', rules: ['ignoreScoreFever', 'sameStamps', 'missesOk', 'dissolveOnMismatch'] }
};

const policyColumns = {
  left: ['allowProxy', 'freeDifficulty', 'highSpeed', 'noFireExtinguish', 'noMaster', 'noExpert', 'allowOffMetaSongs', 'songSelectable', 'watchMv', 'allowCostumeChange', 'noDefaultCostume'],
  right: ['ignoreScoreFever', 'missesOk', 'endOnHostSignal', 'sameStamps', 'dissolveOnMismatch', 'longPlayWelcome', 'leavingMidwayOk', 'noLeavingMidway', 'finishSession', 'waitForAll', 'disconnectOkOnLastSong', 'lowerReqsIfSlow', 'disbandIfSlow']
};

const scoreTierOptions = {
  percentage: '%',
  '☆4': '☆4',
  '☆4スコアアップ': '☆4스업',
};

// Text Functions

const generatePostText = (config, playerSlots, presetType) => {
  const {
    isVeteran, durationType, gameCount, playUntil, selectedSong, customSong,
    roomCode, hostScoreType, hostScoreValue,
    reqScoreType, reqScoreValue, reqScoreOperator, hasSupporter,
    hasEncore, supporterScore, extraInfo, ...policies
  } = config;

  const getSong = () => {
    let song = customSong.trim();
    if (!song) song = selectedSong;
    const isGameCountMode = durationType === 'games' && gameCount;
    if (song && !song.includes('周回') && !isGameCountMode) {
      return song + '周回';
    }
    return song;
  };

  const getDuration = () => {
    if (durationType === 'games') {
      return gameCount ? `${gameCount}回` : '';
    }
    const time = playUntil.trim();
    if (!time) return '';
    if (/^\d{1,2}$/.test(time)) return `${time}時まで`;
    if (/^[^ - ]+$/.test(time)) return time;
    return `${time}まで`;
  };

  const getScore = (type, value, operator = '') => {
    if (type === 'percentage') {
      return value ? `${value}%${operator}` : '';
    }
    return type;
  };

  const getSupportInfo = () => {
    if (!hasSupporter && !hasEncore) return '';

    let statuses = [];
    if (hasSupporter && hasEncore) statuses.push('支援、アンコいます');
    else if (hasSupporter) statuses.push('支援います');
    else if (hasEncore) statuses.push('アンコいます');

    let details = [];
    if (supporterScore) details.push(`${supporterScore}%`);
    if (extraInfo) details.push(extraInfo);

    const detailText = details.length > 0 ? `(${details.join(' ')})` : '';
    return `${statuses.join(' ')}${detailText}\n`;
  };

  const getPolicyText = () => {
    const specialKeys = ['lowerReqsIfSlow', 'disbandIfSlow'];

    const normalPolicies = Object.keys(lobbyPolicies)
      .filter(key => policies[key] && !specialKeys.includes(key))
      .map(key => lobbyPolicies[key].value);

    const specialPolicies = Object.keys(lobbyPolicies)
      .filter(key => policies[key] && specialKeys.includes(key))
      .map(key => lobbyPolicies[key].value);

    let formatted = [];
    for (let i = 0; i < normalPolicies.length; i += 2) {
      if (i + 1 < normalPolicies.length) {
        formatted.push(normalPolicies[i] + ', ' + normalPolicies[i + 1]);
      } else {
        formatted.push(normalPolicies[i]);
      }
    }

    if (specialPolicies.length > 0) {
      specialPolicies.forEach(val => {
        formatted.push(`※${val}`);
      });
    }

    return formatted.join('\n');
  };

  const pFormat = (() => {
    switch (presetType) {
      case 2: return { vSpace: '  ', host: '[主]', req: '[求]', room: '🗝️ ', hashSp: ' ' };
      case 3: return { vSpace: '', host: '主 : ', req: '求 : ', room: '🗝️', hashSp: '  ' };
      case 4: return { vSpace: '   ', host: '❖主', req: '❖求', room: '🔑', hashSp: '\n' };
      case 1:
      default: return { vSpace: ' ', host: '【主】', req: '【求】', room: '🔑', hashSp: ' ' };
    }
  })();

  const template = [
    `${isVeteran ? `ベテラン${pFormat.vSpace}` : ''}${getSong()} ${getDuration()} @${playerSlots || ''}`,
    `${pFormat.room}${(roomCode || '').replace(/[^0-9]/g, '').slice(0, 5).padStart(5, '0')}`,
    `${pFormat.host}${getScore(hostScoreType, hostScoreValue)}`,
    `${pFormat.req}${getScore(reqScoreType, reqScoreValue, reqScoreOperator)}`,
    getSupportInfo(),
    getPolicyText(),
    `#プロセカ協力${pFormat.hashSp}#プロセカ募集`
  ].join('\n');

  return template.replace(/\n{3,}/g, '\n\n').replace(/^\s*\n/gm, '').trim();
};


// Component

function PostGenerator({ setTweetText, presetType, playerSlots, setPlayerSlots }) {
  const [config, setConfig] = useState({
    isVeteran: true,
    durationType: 'games',
    gameCount: '',
    playUntil: '',
    selectedSong: '',
    customSong: '',
    roomCode: '',
    hostScoreType: 'percentage',
    hostScoreValue: '',
    reqScoreType: 'percentage',
    reqScoreValue: '',
    reqScoreOperator: '↑',
    hasSupporter: false,
    hasEncore: false,
    supporterScore: '',
    extraInfo: '',
    ...Object.keys(lobbyPolicies).reduce((acc, key) => ({ ...acc, [key]: false }), {})
  });

  const handleConfigChange = useCallback((key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleEventChange = (e) => {
    const { name, value, type, checked } = e.target;
    let processedValue = type === 'checkbox' ? checked : value;

    if (name === 'roomCode') {
      processedValue = value.replace(/[^0-9]/g, '').slice(0, 5);
    } else if (['hostScoreValue', 'reqScoreValue', 'supporterScore', 'gameCount'].includes(name)) {
      processedValue = value.replace(/[^0-9]/g, '');
    }

    handleConfigChange(name, processedValue);
  };

  const handleSongPresetChange = (presetKey) => {
    const newPolicies = songConfigurations[presetKey]?.rules || songConfigurations['default'].rules;
    const updatedPolicies = Object.keys(lobbyPolicies).reduce((acc, key) => {
      acc[key] = newPolicies.includes(key);
      return acc;
    }, {});

    setConfig(prev => ({
      ...prev,
      selectedSong: presetKey,
      customSong: '',
      ...updatedPolicies
    }));
  };

  useEffect(() => {
    const generatedText = generatePostText(config, playerSlots, presetType);
    setTweetText(generatedText);
  }, [config, playerSlots, presetType, setTweetText]);

  return (
    <>
      <LayoutRow>
        <SongFormSection>
          <SectionHeader>악곡</SectionHeader>
          <ButtonGroup>
            {Object.entries(songConfigurations).filter(([key]) => key !== 'default').map(([key, { label }]) => (
              <SongOptionButton key={key} className={config.selectedSong === key ? 'selected' : ''} onClick={() => handleSongPresetChange(key)}>
                {label}
              </SongOptionButton>
            ))}
          </ButtonGroup>
          <p style={{ textAlign: 'center', margin: '8px 0', color: '#718096', fontSize: '0.9rem' }}>또는</p>
          <TextInput type="text" name="customSong" placeholder="곡명 / 팀명 직접 입력" value={config.customSong} onChange={handleEventChange} />
        </SongFormSection>
        <FormSection>
          <SectionHeader>주회 시간 (선택)</SectionHeader>
          <ButtonGroup>
            <OptionButton className={config.durationType === 'games' ? 'selected' : ''} onClick={() => handleConfigChange('durationType', 'games')}>판수</OptionButton>
            <OptionButton className={config.durationType === 'time' ? 'selected' : ''} onClick={() => handleConfigChange('durationType', 'time')}>시간</OptionButton>
          </ButtonGroup>
          {config.durationType === 'games' ? (
            <TextInput type="tel" name="gameCount" placeholder="(예: 3)" value={config.gameCount} onChange={handleEventChange} />
          ) : (
            <TextInput type="text" name="playUntil" placeholder="(예: 23 or 23:30 / (時)まで 자동추가)" value={config.playUntil} onChange={handleEventChange} />
          )}
        </FormSection>
      </LayoutRow>
      <LayoutRow>
        <FormSection>
          <SectionHeader>@ 모집 인원</SectionHeader>
          <ButtonGroup>
            {[1, 2, 3, 4].map(num => (
              <OptionButton
                key={num}
                className={playerSlots === num ? 'selected' : ''}
                onClick={() => setPlayerSlots(num)}
              >
                {num}
              </OptionButton>
            ))}
          </ButtonGroup>
        </FormSection>
        <FormSection>
          <SectionHeader>🔑 방 번호</SectionHeader>
          <TextInput type="tel" name="roomCode" placeholder="5자리 숫자" value={config.roomCode} onChange={handleEventChange} />
        </FormSection>
      </LayoutRow>

      <FormSection>
        <LayoutRow>
          <div>
            <SectionHeader style={{ margin: '0 0 10px 0' }}>주: 방장 실효치</SectionHeader>
            <ButtonGroup>
              {Object.entries(scoreTierOptions).map(([key, label]) => (
                <OptionButton key={key} className={config.hostScoreType === key ? 'selected' : ''} onClick={() => handleConfigChange('hostScoreType', key)} style={{ flexGrow: label === '☆4스업' ? 1.1 : 1 }}>
                  {label}
                </OptionButton>
              ))}
            </ButtonGroup>
            <TextInput type="tel" name="hostScoreValue" placeholder="실효치 (예: 210)" value={config.hostScoreValue} onChange={handleEventChange} disabled={config.hostScoreType === '☆4' || config.hostScoreType === '☆4スコアアップ'} />
          </div>
          <div>
            <SectionHeader style={{ margin: '0 0 10px 0' }}>모: 모집 조건</SectionHeader>
            <ButtonGroup>
              {Object.entries(scoreTierOptions).map(([key, label]) => (
                <OptionButton key={key} className={config.reqScoreType === key ? 'selected' : ''} onClick={() => handleConfigChange('reqScoreType', key)} style={{ flexGrow: label === '☆4스업' ? 1.1 : 1 }}>
                  {label}
                </OptionButton>
              ))}
            </ButtonGroup>
            <TextInput type="tel" name="reqScoreValue" placeholder="실효치 (예: 200) ↑" value={config.reqScoreValue} onChange={handleEventChange} disabled={config.reqScoreType === '☆4' || config.reqScoreType === '☆4スコアアップ'} />
          </div>
        </LayoutRow>
      </FormSection>

      <FormSection style={{ marginTop: '20px' }}>
        <SectionHeader>헬퍼・앵콜 (선택)</SectionHeader>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap' }}>
          <CheckboxWrapper>
            <input type="checkbox" name="hasSupporter" checked={config.hasSupporter} onChange={handleEventChange} />
            헬퍼 있음
          </CheckboxWrapper>
          <CheckboxWrapper>
            <input type="checkbox" name="hasEncore" checked={config.hasEncore} onChange={handleEventChange} />
            앵콜 있음
          </CheckboxWrapper>
        </div>
        <LayoutRow>
          <TextInput type="tel" name="supporterScore" placeholder="헬퍼 실효치 (예: 275)" value={config.supporterScore} onChange={handleEventChange} />
          <TextInput type="text" name="extraInfo" placeholder="앵콜 종합력/자유 텍스트" value={config.extraInfo} onChange={handleEventChange} />
        </LayoutRow>
      </FormSection>

      <FormSection style={{ marginTop: '20px' }}>
        <SectionHeader>방 규칙</SectionHeader>
        <PolicyGrid>
          <PolicyColumn>
            {policyColumns.left.map(key => (
              <CheckboxWrapper key={key}><input type="checkbox" name={key} checked={config[key]} onChange={handleEventChange} />{lobbyPolicies[key].text}</CheckboxWrapper>
            ))}
          </PolicyColumn>
          <PolicyColumn>
            <CheckboxWrapper><input type="checkbox" name="isVeteran" checked={config.isVeteran} onChange={handleEventChange} />베테랑</CheckboxWrapper>
            {policyColumns.right.map(key => (
              <CheckboxWrapper key={key}><input type="checkbox" name={key} checked={config[key]} onChange={handleEventChange} />{lobbyPolicies[key].text}</CheckboxWrapper>
            ))}
          </PolicyColumn>
        </PolicyGrid>
      </FormSection>
    </>
  );
}

export default PostGenerator;
